import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["value", "iconFn", "maxValue"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { IconStar, IconStar50, IconStarOutline } from '../Icon/index';
import { StyledRatingStars } from './RatingStars.styles';
var roundDownToHalfStars = function roundDownToHalfStars(val, i) {
  var Icon = IconStarOutline;
  if (val === 1) {
    Icon = IconStar;
  } else if (val >= 0.5) {
    Icon = IconStar50;
  }
  return /*#__PURE__*/React.createElement(Icon, {
    "aria-hidden": "true",
    key: i,
    focusable: "false"
  });
};

/**
 * @since 8.4.0
 */
export var RatingStars = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var value = _ref.value,
    iconFn = _ref.iconFn,
    maxValue = _ref.maxValue,
    rest = _objectWithoutProperties(_ref, _excluded);
  var stars = [];
  for (var i = 0; i < maxValue; i += 1) {
    var val = void 0;
    if (value - i >= 1) {
      val = 1;
    } else if (value - i <= 0) {
      val = 0;
    } else {
      val = i === 0 ? value : value % i;
    }
    var starIcon = iconFn(val, i);
    stars.push(starIcon);
  }
  return /*#__PURE__*/React.createElement(StyledRatingStars, _extends({
    ref: forwardedRef,
    value: value
  }, rest), stars);
});
RatingStars.displayName = 'RatingStars';
RatingStars.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Used to convey the rating to screenreaders. Ex: "3.6 out of 5 stars."
   * If you're already including this text in an accompanying label use
   * [`aria-labelledby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-labelledby_attribute)
   * instead.
   */
  'aria-label': PropTypes.string,
  /** Takes a rating value from 0 to 5 or null. */
  'value': PropTypes.number,
  /**
   * A function that translates a value into a star icon component.
   *
   * It takes as args a value from 0 thru 1 and the star's index within the group.
   * It then returns an Icon component (ex: IconStar, IconStar50).
   *
   * The default function rounds ***down*** to the nearest half star.
   * Ex: 0.49 => empty star, 0.99 => half star
   */
  'iconFn': PropTypes.func,
  /**
   * The highest rating this set of stars can represent. Customizing this
   * value is not encouraged and should only be used in rare cases.
   *
   * @ignore
   */
  'maxValue': PropTypes.number,
  /**
   * A WAI-ARIA role is required in order to use `aria-label` or `aria-labelledby`.
   * We default to using
   * [`role="img"`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Role_Img)
   * so that screenreaders treat the group of stars as a single image.
   */
  'role': PropTypes.string
} : {};
RatingStars.defaultProps = {
  'aria-label': undefined,
  'value': null,
  'iconFn': roundDownToHalfStars,
  'maxValue': 5,
  'role': 'img'
};